import { useEffect, useCallback, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { absentText, getOnTimeStatusStyle, getRowDrillUrl, rowDrillProps, ROW_DRILL_CLASS } from './drilldownUtils';
import { poNumberHref } from '../common/PoHeaderLink';
import { Button } from '../ui/button';
import { Pagination } from '../ui/pagination';
import {
  Archive,
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDrilldown } from '../../contexts/DrilldownContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { drilldownAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { pageCountFromResponse, type PageSizeMemory } from '../../hooks/usePagination';
import {
  exportTruncationMessage,
  readExportTruncation,
  type ExportTruncation,
} from '../../lib/exportTruncation';
import ExportTruncationBanner from '../export/ExportTruncationBanner';

// Linkable field configuration
const LINKABLE_FIELDS: Record<string, {
  urlPattern: (value: string, record: Record<string, unknown>) => string;
  tab?: string;
}> = {
  // GRN fields - open in delivery tab filtered to that GRN
  'grn_number': {
    urlPattern: (value, _record) => `/purchasing?tab=delivery&search=${encodeURIComponent(value)}`,
    tab: 'delivery',
  },
  // CR-7278: PO number opens its line's detail when the row is one line,
  // else the PO header.
  'po_line__po__po_number': {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  // CR-1154: line number - deep-links to the PO line item detail via its PK.
  // Never key this on `record.id`: that is the metric's base-model PK
  // (GRN / NCR / appeal), so the backend emits an explicit `po_line_id`.
  // Falls back to the PO-filtered list when no line resolves.
  'po_line__line_number': {
    urlPattern: (_value, record) => {
      const poLineId = record['po_line_id'];
      if (poLineId != null) {
        return `/purchasing?tab=po_lines&line=${String(poLineId)}`;
      }
      const poNumber = record['po_line__po__po_number'];
      return `/purchasing?tab=po_lines&search=${encodeURIComponent(String(poNumber || ''))}`;
    },
    tab: 'po_lines',
  },
  // CR-7278: PO number fields open the row's line, else the PO header.
  'po__po_number': {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  'po_number': {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  'grn__po_line__po__po_number': {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  // NCR fields - open specific NCR detail page
  'ncr_number': {
    urlPattern: (_value, record) => `/quality/ncr/${record['id']}/edit`,
  },
};

// Metrics that have specialized modal components and should not use the generic DrilldownModal
const SPECIALIZED_METRICS = ['otif', 'otif_lines', 'otd', 'otd_lines'];

export default function DrilldownModal() {
  // CR-6307: render drilldown date cells via the preference-aware formatter.
  const { formatDate, preferences } = useFormatters();
  const {
    state,
    closeDrilldown,
    setPage,
    setPageSize,
    setData,
    setMetricConfig,
    setError,
    setLoading,
    setActiveTab,
    setTabCounts,
  } = useDrilldown();
  // CR-7279: the page size the server actually served for the current request size.
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);

  // CR-3384: Get view context for hierarchy-based filtering
  const { viewLevel, getViewQueryParams } = useView();

  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  // CR-7432: counts from the last export, when it was cut short by the row cap.
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Fetch metric config when modal opens
  useEffect(() => {
    const fetchConfig = async () => {
      if (!state.metric || !state.isOpen) return;

      try {
        const response = await drilldownAPI.getConfig();
        const metricConfig = response.data.find(m => m.key === state.metric);

        if (metricConfig) {
          setMetricConfig({
            displayName: metricConfig.display_name,
            description: metricConfig.description,
            filterConfig: metricConfig.filter_config || [],
            groupings: metricConfig.groupings || [],
            supportsTabs: metricConfig.supports_tabs || false,
            tabs: metricConfig.tabs || [],
          });
        }
        setConfigLoaded(true);
      } catch (err) {
        console.error('Failed to fetch metric config:', err);
        setConfigLoaded(true);
      }
    };

    if (state.isOpen && !configLoaded) {
      fetchConfig();
    }
  }, [state.isOpen, state.metric, configLoaded, setMetricConfig]);

  // Reset config loaded when modal closes
  useEffect(() => {
    if (!state.isOpen) {
      setConfigLoaded(false);
    }
  }, [state.isOpen]);

  // Fetch data when modal opens or filters/page change
  const fetchData = useCallback(async () => {
    if (!state.metric || !state.isOpen) return;

    setLoading(true);
    try {
      // Apply tab filters if tabs are supported
      let tabFilter = {};
      if (state.supportsTabs && state.activeTab) {
        const activeTabConfig = state.tabs.find(t => t.key === state.activeTab);
        if (activeTabConfig) {
          tabFilter = activeTabConfig.filter;
        }
      }

      // CR-3384: Include view context params for hierarchy-based filtering
      const viewParams = getViewQueryParams();
      const response = await drilldownAPI.getDetails(state.metric, {
        ...state.filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        // Wave 3d: the SAME display currency the dashboard tile was fetched
        // with (useDashboardData sends preferences.currency), so the rows,
        // the tile and this modal's own heading are one currency.
        display_currency: preferences?.currency || undefined,
        page: state.currentPage,
        page_size: state.pageSize,
      });

      const { results, count } = response.data;
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(count, state.pageSize, results.records.length, state.currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setData({
        records: results.records,
        fields: results.fields,
        displayName: results.display_name,
        totalCount: count,
        totalPages: paged.totalPages,
      });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load drill-down data');
    }
  }, [state.metric, state.isOpen, state.filters, state.currentPage, state.pageSize, state.supportsTabs, state.activeTab, state.tabs, viewLevel, getViewQueryParams, setData, setError, setLoading]);

  useEffect(() => {
    if (state.isOpen && state.metric && configLoaded) {
      fetchData();
    }
  }, [state.isOpen, state.metric, state.filters, state.currentPage, state.pageSize, state.activeTab, configLoaded, viewLevel, fetchData]);

  // CR-7432: the banner names a count for the cohort it was exported from.
  // Once the metric, tab or filters move, those numbers describe a file nobody
  // is looking at any more — a screen stating something untrue, which is the
  // complaint this ticket started from.
  useEffect(() => {
    setTruncation(null);
  }, [state.metric, state.activeTab, state.filters]);

  // Fetch tab counts when tabs are available
  useEffect(() => {
    const fetchTabCounts = async () => {
      if (!state.metric || !state.supportsTabs || state.tabs.length === 0) return;

      try {
        const viewParams = getViewQueryParams();
        const counts: { [key: string]: number } = {};

        for (const tab of state.tabs) {
          const response = await drilldownAPI.getDetails(state.metric, {
            ...state.filters,
            ...tab.filter,
            view_level: viewLevel,
            ...viewParams,
            page: 1,
            page_size: 1,
          });
          // CR-4560: Use aggregate_count (e.g. SUM of qty) if available, else record count
          counts[tab.key] = response.data.aggregate_count ?? response.data.count;
        }

        setTabCounts(counts);
      } catch (err) {
        console.error('Failed to fetch tab counts:', err);
      }
    };

    if (state.supportsTabs && state.isOpen && configLoaded) {
      fetchTabCounts();
    }
  }, [state.metric, state.supportsTabs, state.tabs, state.isOpen, state.filters, configLoaded, viewLevel, getViewQueryParams, setTabCounts]);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (!state.metric) return;

    setExporting(true);
    setShowExportMenu(false);
    try {
      // CR-4581: Include active tab filter so export matches displayed data
      let tabFilter = {};
      if (state.supportsTabs && state.activeTab) {
        const activeTabConfig = state.tabs.find(t => t.key === state.activeTab);
        if (activeTabConfig) tabFilter = activeTabConfig.filter;
      }
      // CR-3384: Include view context params for hierarchy-based filtering
      const viewParams = getViewQueryParams();
      const response = await drilldownAPI.exportData(state.metric, format, {
        ...state.filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        // Same currency as the grid, or the file disagrees with the screen.
        display_currency: preferences?.currency || undefined,
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${state.metric}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // CR-7432: the file downloads whatever the server had to leave out, and
      // nothing here said so. Warn only — a complete export stays as quiet as
      // it has always been.
      const truncated = readExportTruncation(response);
      setTruncation(truncated);
      if (truncated) toast.warning(exportTruncationMessage(truncated));
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error(`Export failed. The ${format.toUpperCase()} file could not be generated — please try again.`);
    } finally {
      setExporting(false);
    }
  };

  // CR-7270 rework (Aaron, 18 Sep): an absent value reads '-' on every row,
  // frozen or live (drilldownUtils.absentText) — the cached-data pill above the
  // grid is what says these rows are month-end data.
  const formatValue = (value: unknown, record?: Record<string, unknown>): string => {
    if (value === null || value === undefined) return absentText(record);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      // Check if it looks like a currency value
      if (Number.isInteger(value) && value > 1000) {
        return value.toLocaleString();
      }
      return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
    }
    // Check if it's a date string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return formatDate(value);
    }
    return String(value);
  };

  // Render cell value - with link if field is linkable
  const renderCellValue = (fieldKey: string, value: unknown, record: Record<string, unknown>) => {
    const formattedValue = formatValue(value, record);

    // Handle long description fields - truncate and add View link
    const descriptionFields = ['description', 'defect_description', 'part_description'];
    if (descriptionFields.includes(fieldKey)) {
      const text = String(value || '-');
      const maxLength = 80;
      const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

      // If NCR record, add View link to NCR detail page
      if (record['id'] && text !== '-') {
        const ncrUrl = `/quality/ncr/${record['id']}/edit`;
        return (
          <div className="max-w-xs">
            <div className="break-words whitespace-normal">{truncated}</div>
            {text.length > maxLength && (
              <a
                href={ncrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 hover:underline inline-flex items-center gap-1 text-xs mt-1 font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                View full details
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        );
      }

      return <div className="max-w-xs break-words whitespace-normal">{truncated}</div>;
    }

    // Render on_time_status as colored badge
    if (fieldKey === 'on_time_status' && value) {
      const style = getOnTimeStatusStyle(value);
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}>
          {style.label}
        </span>
      );
    }

    // Check if this field should be a link
    const linkConfig = LINKABLE_FIELDS[fieldKey];
    if (linkConfig && value) {
      const url = linkConfig.urlPattern(String(value), record);
      // CR-1154: NCR links open in a new tab, consistent with the PO/GRN links
      // in this modal. The old CR-6018 same-tab workaround (a fresh tab bounced
      // to /login on empty sessionStorage) is superseded by the CR-6282
      // BroadcastChannel session handoff, which lets a new tab recover the live
      // session from an already-open tab.
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 hover:underline inline-flex items-center gap-1 font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {formattedValue}
          <ExternalLink className="h-3 w-3" />
        </a>
      );
    }

    return formattedValue;
  };

  // Row selection helpers
  const toggleRowSelection = (idx: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleAllRows = () => {
    if (selectedRows.size === state.records.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(state.records.map((_, i) => i)));
    }
  };

  // Reset selection on page/filter change
  if (selectedRows.size > 0 && state.records.length === 0) {
    setSelectedRows(new Set());
  }

  // Don't render for metrics that have specialized modals (OTIF, OTD)
  // These metrics use OTIFDrilldownModal and OTDDrilldownModal instead
  if (state.metric && SPECIALIZED_METRICS.includes(state.metric)) {
    return null;
  }


  // CR-7270: a past month's arrears is a frozen month-end balance, and every
  // value in those rows is as at that boundary — not today's book. The server
  // stamps each frozen row; the modal says so, because nothing in a rendered
  // row can.
  const frozenAsAt = state.records
    .map((record) => (record as { frozen_as_at?: string | null }).frozen_as_at)
    .find((value): value is string => Boolean(value));

  return (
    <Dialog open={state.isOpen} onOpenChange={(open) => !open && closeDrilldown()}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {state.displayName || 'Drill-Down Data'}
                {state.contextLabel && (
                  <span className="text-muted-foreground font-normal ml-2">
                    - {state.contextLabel}
                  </span>
                )}
                {state.headline && (
                  <span className="text-muted-foreground font-normal ml-2" data-testid="drilldown-headline">
                    · {state.headline.label} {state.headline.value}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                {/* Descriptions may carry a {base_currency} token — resolve it to
                    the effective reporting/display currency (the one every figure
                    in the modal is formatted in), never a hard-coded code. */}
                {state.description && (
                  <span className="mr-2">
                    {state.description.replace('{base_currency}', preferences?.currency || '')}
                  </span>
                )}
                {state.totalCount.toLocaleString()} records found
              </DialogDescription>
              {frozenAsAt && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  <Archive className="h-3.5 w-3.5" />
                  Cached month-end data as at {formatDate(frozenAsAt)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Selected count */}
              {selectedRows.size > 0 && (
                <span className="text-sm text-muted-foreground">
                  {selectedRows.size} selected
                </span>
              )}

              {/* Export Dropdown */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting || state.records.length === 0}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('xlsx')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <ExportTruncationBanner
            truncation={truncation}
            onDismiss={() => setTruncation(null)}
          />
        </DialogHeader>

        {/* Tabs for metrics that support them */}
        {state.supportsTabs && state.tabs.length > 0 && (
          <div className="border-b">
            <div className="flex gap-1">
              {state.tabs.map((tab) => {
                const count = state.tabCounts[tab.key];
                const isActive = state.activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2.5 font-medium text-sm border-b-2 transition-colors ${
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    {tab.label}
                    {count !== undefined && (
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {count.toLocaleString()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content Area - min-h prevents layout shift during loading that causes visual jerking */}
        <div className="flex-1 min-h-[400px] overflow-auto">
          {state.loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data...</span>
            </div>
          ) : state.error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{state.error}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : state.records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p>No records found</p>
            </div>
          ) : (
            <div>
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-2 w-10">
                      <input
                        type="checkbox"
                        checked={state.records.length > 0 && selectedRows.size === state.records.length}
                        onChange={toggleAllRows}
                        className="rounded border-gray-300"
                      />
                    </th>
                    {state.fields.map((field) => (
                      <th
                        key={field.field}
                        className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {state.records.map((record, idx) => (
                    <tr
                      key={record.id || idx}
                      className={`border-b hover:bg-muted/30 transition-colors ${selectedRows.has(idx) ? 'bg-primary/5' : ''} ${getRowDrillUrl(record) ? ROW_DRILL_CLASS : ''}`}
                      {...rowDrillProps(getRowDrillUrl(record))}
                    >
                      <td className="py-3 px-2 w-10">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(idx)}
                          onChange={() => toggleRowSelection(idx)}
                          className="rounded border-gray-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {state.fields.map((field) => {
                        const descriptionFields = ['description', 'defect_description', 'part_description'];
                        const isDescription = descriptionFields.includes(field.field);

                        return (
                          <td
                            key={field.field}
                            className={`py-3 px-4 text-sm ${isDescription ? '' : 'whitespace-nowrap'}`}
                          >
                            {renderCellValue(field.field, record[field.field], record)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!state.loading && !state.error && state.totalPages > 1 && (
          <div className="flex-shrink-0 -mt-4">
            <Pagination
              currentPage={state.currentPage}
              totalPages={state.totalPages}
              totalCount={state.totalCount}
              itemsPerPage={state.pageSize} servedPerPage={servedPageRef.current?.served}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              itemLabel="records"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
